/**
 * Single source of truth for runtime configuration on the client.
 *
 * All values originate from Vite environment variables (prefixed VITE_) so
 * deployment-specific information lives outside source control. The shape
 * of the object is intentionally flat to make consumption straightforward.
 */

const env = import.meta.env;

const config = {
    network: {
        name: env.VITE_NETWORK_NAME || "Base Sepolia",
        chainId: Number(env.VITE_CHAIN_ID || 84532),
        rpcUrl: env.VITE_RPC_URL || "https://sepolia.base.org",
        nativeCurrency: {
            name: env.VITE_NATIVE_SYMBOL || "ETH",
            symbol: env.VITE_NATIVE_SYMBOL || "ETH",
            decimals: 18
        },
        blockExplorerUrl: env.VITE_BLOCK_EXPLORER_URL || "https://sepolia.basescan.org"
    },
    backendUrl: env.VITE_BACKEND_URL || "http://127.0.0.1:4000",
    // WalletConnect cloud project id. Generated at https://cloud.walletconnect.com.
    // Without it the WalletConnect option in the connect modal stays disabled
    // — the SDK refuses to initialise.
    walletConnectProjectId: env.VITE_WALLETCONNECT_PROJECT_ID || "",
    contracts: {
        bond: env.VITE_BOND_ADDRESS || "",
        safeVault: env.VITE_SAFE_VAULT_ADDRESS || "",
        treasury: env.VITE_TREASURY_ADDRESS || "",
        depositNft: env.VITE_DEPOSIT_NFT_ADDRESS || "",
        lending: env.VITE_LENDING_ADDRESS || "",
        turbo: env.VITE_TURBO_ADDRESS || "",
        autoCompound: env.VITE_AUTO_COMPOUND_ADDRESS || "",
        reinvestment: env.VITE_REINVESTMENT_ADDRESS || "",
        gil: env.VITE_GIL_ADDRESS || "",
        tokenBuyRouter: env.VITE_TOKEN_BUY_ROUTER_ADDRESS || "",
        // Uniswap V3 router for the user-facing Buy/Sell $GILD marketplace.
        // Optional: when unset the Marketplace page discovers it on-chain from
        // `tokenBuyRouter.swapRouter()` (the protocol's token-buy leg uses the
        // same router), so this only needs setting to override that lookup.
        uniswapRouter: env.VITE_UNISWAP_ROUTER_ADDRESS || "",
        usdc: env.VITE_USDC_ADDRESS || "",
        tvt: env.VITE_TVT_ADDRESS || "",
        priceBandExecutor: env.VITE_PRICE_BAND_EXECUTOR_ADDRESS || "",
        treasuryExecutor: env.VITE_TREASURY_EXECUTOR_ADDRESS || "",
        marketingEngine: env.VITE_MARKETING_ENGINE_ADDRESS || "",
        selfPayingNetting: env.VITE_SELF_PAYING_NETTING_ADDRESS || "",
        dePegGuard: env.VITE_DEPEG_GUARD_ADDRESS || "",
        governance: env.VITE_GOVERNANCE_ADDRESS || ""
    },
    protocol: {
        usdcDecimals: 6,
        minDepositUsdc: 100,
        annualRateBps: 2000,
        fixedTermDays: 1095,
        earlyExitPenaltyBps: 2000,
        safeVaultBps: 8000,
        maxLtvBps: 7500,
        liquidationThresholdBps: 8500,
        loanAprBps: 1500,
        // Standard Auto-Reinvest sweep floor. A Standard pool mints a fresh
        // plain Term Deposit, so the protocol minimum ($100) is the only
        // constraint.
        autoCompoundTriggerUsdc: 100,
        // §M6 (Jul 2026 client review) — Turbo Auto-Reinvest sweep floor.
        // A Turbo sweep runs the 12-round leverage loop on the freshly
        // minted deposit; below $10k each rung rounds down to dust and the
        // headline 27.5% net yield is unreachable (the loop stalls on the
        // $100 protocol minimum long before round 12). So a Turbo POOL only
        // sweeps once combined yield clears $10,000 — Standard still sweeps
        // at $100.
        turboCompoundTriggerUsdc: 10_000,
        // Minimum single deposit for the Turbo product to actually reach the
        // advertised 27.5% net. Same reason as above: the recursive borrow
        // loop needs headroom. Below this the Deposit page warns instead of
        // quoting 27.5%.
        turboMinDepositUsdc: 10_000,
        // §Code-Review / Phase-2 (Jul 2026 client review) — secondary-sale
        // royalty on Term Deposit NFTs, cut from 5% to 2%. Mirrors
        // DepositNFT.ROYALTY_BPS on-chain.
        nftRoyaltyBps: 200
    },
    restrictedRegions: ["US", "SG",],
    EMAIL_REGEX :/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i,
    TOKEN_REGEX :/(\[[^\]]+\]\([^)]+\)|\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b|\bhttps?:\/\/[^\s]+|\bwww\.[^\s]+|(?<!\w)\/[a-zA-Z0-9\-_/]+(?!\w))/gi


};

export default config;
